import React, { useState } from "react";
import { Fieldset, Field, Label } from "../../design/fieldset";
import { Input } from "../../design/input";
import { Button } from "../../design/button";
import { IncomeInfo } from "../../models/IncomeInfo";
import { ParticipantService } from "../../services/ParticipantService";

interface Props {
  participantId: string;
  value?: IncomeInfo;
  onFormSubmitted?: () => void;
}

const IncomeForm: React.FC<Props> = ({
  participantId,
  value,
  onFormSubmitted,
}) => {
  const [formState, setFormState] = useState<IncomeInfo>(value ?? {});
  const [isSaving, setIsSaving] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    await ParticipantService.updateIncome(participantId, formState);
    onFormSubmitted?.();
  };

  return (
    <form onSubmit={handleSubmit}>
      <Fieldset className="space-y-4" disabled={isSaving}>
        <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 sm:gap-4">
          {[
            "salary",
            "overtime",
            "bonuses",
            "publicAssistance",
            "spousalSupport",
            "partnerSupport",
            "pension",
            "socialSecurity",
            "unemployment",
            "workersComp",
          ].map((field) => (
            <Field key={field}>
              <Label htmlFor={field}>{field.replace(/([A-Z])/g, " $1")}</Label>
              <Input
                id={field}
                type="number"
                value={(formState as any)[field] ?? ""}
                onChange={(e) =>
                  setFormState({
                    ...formState,
                    [field]: Number(e.target.value),
                  })
                }
                disabled={isSaving}
              />
            </Field>
          ))}

          {/* Disability object */}
          <Field>
            <Label htmlFor="disabilitySsi">Disability – SSI</Label>
            <Input
              id="disabilitySsi"
              type="number"
              value={formState.disability?.ssi ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  disability: {
                    ...formState.disability,
                    ssi: Number(e.target.value),
                  },
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="disabilityState">Disability – State</Label>
            <Input
              id="disabilityState"
              type="number"
              value={formState.disability?.state ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  disability: {
                    ...formState.disability,
                    state: Number(e.target.value),
                  },
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="disabilityPrivate">Disability – Private</Label>
            <Input
              id="disabilityPrivate"
              type="number"
              value={formState.disability?.private ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  disability: {
                    ...formState.disability,
                    private: Number(e.target.value),
                  },
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="otherIncomeSource">Other Income – Source</Label>
            <Input
              id="otherIncomeSource"
              value={formState.otherIncome?.source || ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  otherIncome: {
                    ...formState.otherIncome,
                    source: e.target.value,
                  },
                })
              }
              disabled={isSaving}
            />
          </Field>

          <Field>
            <Label htmlFor="otherIncomeAmount">Other Income – Amount</Label>
            <Input
              id="otherIncomeAmount"
              type="number"
              value={formState.otherIncome?.amount ?? ""}
              onChange={(e) =>
                setFormState({
                  ...formState,
                  otherIncome: {
                    ...formState.otherIncome,
                    amount: Number(e.target.value),
                  },
                })
              }
              disabled={isSaving}
            />
          </Field>
        </div>

        <div className="flex justify-end pt-4">
          <Button type="submit" disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Income"}
          </Button>
        </div>
      </Fieldset>
    </form>
  );
};

export default IncomeForm;
